import { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, IconButton, Paper, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { usePlaidLink } from 'react-plaid-link';
import { useTranslation } from 'react-i18next';
import { getLinkToken } from '../../../api/plaid/get-link-token';
import { exchangeToken } from '../../../api/plaid/exchange-token';
import { disconnectConnection } from '../../../api/plaid/disconnect-connection';
import { PlaidConnectionStatus, UserType } from '../../../types';

export type SnapshotMode = 'ONE_TIME' | 'MONTHLY';

interface ConnectedPanelProps {
  connection: PlaidConnectionStatus;
  onRemove: () => void;
  isRemoving: boolean;
}

const ConnectedPanel = ({
  connection,
  onRemove,
  isRemoving,
}: ConnectedPanelProps) => {
  const { t } = useTranslation('components', { keyPrefix: 'survey-questions.plaid-connect' });

  const accountsLine = connection.accountCount > 0
    ? t(connection.accountCount === 1 ? 'accounts_linked_one' : 'accounts_linked_other', { total: connection.accountCount })
    : null;
  const connectedLine = moment(connection.connectedAt).isValid()
    ? t('connected_on', { date: moment(connection.connectedAt).format('ll') })
    : null;
  const detailLine = [ accountsLine, connectedLine ].filter(Boolean).join(' · ');

  return (
    <Paper
      elevation={0}
      sx={{
        alignItems: 'flex-start',
        backgroundColor: (theme) => alpha(theme.palette.success.main, 0.08),
        borderColor: 'success.main',
        borderLeft: '4px solid',
        borderRadius: 1,
        display: 'flex',
        gap: 1.5,
        padding: 2,
        position: 'relative',
      }}
    >
      <CheckCircleRoundedIcon sx={{
        color: 'success.main',
        marginTop: '2px',
      }}
      />
      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{ fontWeight: 600 }}
          variant='subtitle2'
        >
          {connection.institutionName}
        </Typography>
        {detailLine
          ? (
            <Typography
              color='text.secondary'
              variant='body2'
            >
              {detailLine}
            </Typography>
            )
          : null}
      </Box>
      <IconButton
        aria-label={t('button_remove')}
        disabled={isRemoving}
        size='small'
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
        }}
        onClick={onRemove}
      >
        {isRemoving ? <CircularProgress size={16} /> : <CloseIcon fontSize='small' />}
      </IconButton>
    </Paper>
  );
};

type LinkingPanelStatus = 'consent' | 'requesting-token' | 'open' | 'exchanging' | 'error';

type LinkingErrorKind = 'link-token' | 'exchange' | 'generic';

interface LinkingPanelProps {
  urlSlug: string;
  publicId: string;
  questionName: string;
  snapshotMode: SnapshotMode;
  userType: UserType;
  onLinked: (connection: PlaidConnectionStatus) => void;
  onCancel: () => void;
}

const LinkingPanel = ({
  urlSlug,
  publicId,
  questionName,
  snapshotMode,
  userType,
  onLinked,
  onCancel,
}: LinkingPanelProps) => {
  const { t } = useTranslation('components', { keyPrefix: 'survey-questions.plaid-connect' });
  const [ status, setStatus ] = useState<LinkingPanelStatus>('consent');
  const [ linkToken, setLinkToken ] = useState<string | null>(null);
  const [ errorKind, setErrorKind ] = useState<LinkingErrorKind | null>(null);

  const handleSuccess = async (publicToken: string | null, metadata: { institution: { name: string } | null; accounts: unknown[] }) => {
    if (!publicToken) {
      setErrorKind('exchange');
      setStatus('error');
      return;
    }

    setStatus('exchanging');

    const institutionName = metadata.institution?.name ?? '';
    const accountCount = metadata.accounts?.length ?? 0;

    try {
      const connectionStatus = await exchangeToken(urlSlug, publicId, {
        publicToken,
        institutionName,
        accountCount,
        questionName,
      }, userType);

      if (!connectionStatus.institutionName || !connectionStatus.connectionId) {
        setErrorKind('exchange');
        setStatus('error');
        return;
      }

      onLinked(connectionStatus);
    } catch {
      setErrorKind('exchange');
      setStatus('error');
    }
  };

  const handleExit = (error: { error_type: string } | null) => {
    if (error) {
      setErrorKind('generic');
      setStatus('error');
    } else {
      setStatus('consent');
      setLinkToken(null);
    }
  };

  const {
    ready, open,
  } = usePlaidLink({
    token: linkToken,
    onSuccess: handleSuccess,
    onExit: handleExit,
  });

  useEffect(() => {
    if (status === 'open' && ready && linkToken) {
      open();
    }
  }, [
    status,
    ready,
    linkToken,
    open,
  ]);

  const handleLinkClick = async () => {
    setStatus('requesting-token');
    setErrorKind(null);

    try {
      const response = await getLinkToken(urlSlug, publicId, userType);
      setLinkToken(response.link_token);
      setStatus('open');
    } catch {
      setErrorKind('link-token');
      setStatus('error');
    }
  };

  const handleRetryClick = () => {
    setErrorKind(null);
    setStatus('consent');
  };

  const isLinking = status === 'requesting-token' || status === 'open' || status === 'exchanging';
  const disclosureKey = snapshotMode === 'MONTHLY' ? 'consent_disclosure_monthly' : 'consent_disclosure_one_time';

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        padding: 2,
        position: 'relative',
      }}
    >
      <IconButton
        aria-label={t('button_cancel')}
        disabled={isLinking}
        size='small'
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
        }}
        onClick={onCancel}
      >
        <CloseIcon fontSize='small' />
      </IconButton>
      <Typography
        sx={{ marginBottom: 1 }}
        variant='body2'
      >
        {t('consent_body')}
      </Typography>
      <Typography
        sx={{ marginBottom: 1.5 }}
        variant='body2'
      >
        {t(disclosureKey)}
      </Typography>
      {status === 'error'
        ? (
          <Box>
            <Typography
              color='error'
              sx={{ marginBottom: 1 }}
              variant='body2'
            >
              {t(errorKind === 'link-token' ? 'error_link_token' : (errorKind === 'exchange' ? 'error_exchange' : 'error_generic'))}
            </Typography>
            <Button
              color='primary'
              size='small'
              variant='contained'
              onClick={handleRetryClick}
            >
              {t('button_retry')}
            </Button>
          </Box>
          )
        : (
          <Button
            color='primary'
            disabled={isLinking}
            size='small'
            startIcon={isLinking ? <CircularProgress size={14} /> : undefined}
            variant='contained'
            onClick={handleLinkClick}
          >
            {isLinking ? t('button_linking') : t('button_link')}
          </Button>
          )}
    </Paper>
  );
};

export interface PlaidConnectProps {
  value?: PlaidConnectionStatus[];
  onConnectionsChanged: (connections: PlaidConnectionStatus[]) => void | Promise<void>;
  urlSlug: string;
  publicId: string;
  questionName: string;
  snapshotMode: SnapshotMode;
  maxPanelCount: number;
  isReadOnly: boolean;
  currentUserType: UserType;
}

export const PlaidConnect = ({
  value = undefined,
  onConnectionsChanged,
  urlSlug,
  publicId,
  questionName,
  snapshotMode,
  maxPanelCount,
  isReadOnly,
  currentUserType,
}: PlaidConnectProps) => {
  const { t } = useTranslation('components', { keyPrefix: 'survey-questions.plaid-connect' });
  const [ connections, setConnections ] = useState<PlaidConnectionStatus[]>(value ?? []);
  const [ showLinkingPanel, setShowLinkingPanel ] = useState(false);
  const [ removingId, setRemovingId ] = useState<string | null>(null);
  const [ removeError, setRemoveError ] = useState<string | null>(null);
  const [ saveError, setSaveError ] = useState(false);

  const handleLinked = async (connectionStatus: PlaidConnectionStatus) => {
    const updatedConnections = [ ...connections, connectionStatus ];
    setConnections(updatedConnections);
    setShowLinkingPanel(false);
    setSaveError(false);

    try {
      await onConnectionsChanged(updatedConnections);
    } catch {
      setSaveError(true);
    }
  };

  const handleRemove = async (connectionId: string) => {
    setRemovingId(connectionId);
    setRemoveError(null);
    setSaveError(false);

    try {
      await disconnectConnection(urlSlug, publicId, connectionId, currentUserType);
    } catch {
      setRemoveError(connectionId);
      setRemovingId(null);
      return;
    }

    const remainingConnections = connections.filter((connection) => connection.connectionId !== connectionId);
    setConnections(remainingConnections);
    setRemovingId(null);

    try {
      await onConnectionsChanged(remainingConnections);
    } catch {
      setSaveError(true);
    }
  };

  if (isReadOnly) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
      >
        {connections.length > 0
          ? connections.map((connection) => (
            <Paper
              key={connection.connectionId}
              elevation={0}
              sx={{
                alignItems: 'flex-start',
                backgroundColor: (theme) => alpha(theme.palette.success.main, 0.08),
                borderColor: 'success.main',
                borderLeft: '4px solid',
                borderRadius: 1,
                display: 'flex',
                gap: 1.5,
                padding: 2,
              }}
            >
              <CheckCircleRoundedIcon sx={{
                color: 'success.main',
                marginTop: '2px',
              }}
              />
              <Box>
                <Typography
                  sx={{ fontWeight: 600 }}
                  variant='subtitle2'
                >
                  {connection.institutionName}
                </Typography>
              </Box>
            </Paper>
          ))
          : (
            <Typography variant='body2'>
              {t('not_connected')}
            </Typography>
            )}
      </Box>
    );
  }

  const ownConnections = connections.filter((connection) => connection.userType === currentUserType);
  const otherConnections = connections.filter((connection) => connection.userType !== currentUserType);
  const canAddMore = ownConnections.length < maxPanelCount && !showLinkingPanel;
  const isRemoving = Boolean(removingId);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
    }}
    >
      {otherConnections.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            alignItems: 'center',
            backgroundColor: (theme) => alpha(theme.palette.grey[100], 0.6),
            borderColor: 'divider',
            borderLeft: '4px solid',
            borderRadius: 1,
            display: 'flex',
            gap: 1.5,
            padding: 2,
          }}
        >
          <CheckCircleRoundedIcon sx={{
            color: 'text.secondary',
            marginTop: '2px',
          }}
          />
          <Typography
            color='text.secondary'
            variant='subtitle2'
          >
            {t(otherConnections.length === 1 ? 'other_connections_one' : 'other_connections_other', { count: otherConnections.length })}
          </Typography>
        </Paper>
      )}
      {ownConnections.map((connection) => (
        <Box key={connection.connectionId}>
          <ConnectedPanel
            connection={connection}
            isRemoving={removingId === connection.connectionId}
            onRemove={() => handleRemove(connection.connectionId)}
          />
          {removeError === connection.connectionId
            ? (
              <Typography
                color='error'
                sx={{ marginTop: 0.5 }}
                variant='caption'
              >
                {t('error_disconnect')}
              </Typography>
              )
            : null}
        </Box>
      ))}
      {showLinkingPanel && (
        <LinkingPanel
          publicId={publicId}
          questionName={questionName}
          snapshotMode={snapshotMode}
          urlSlug={urlSlug}
          userType={currentUserType}
          onCancel={() => setShowLinkingPanel(false)}
          onLinked={handleLinked}
        />
      )}
      {saveError && (
        <Typography
          color='warning.main'
          variant='caption'
        >
          {t('error_save')}
        </Typography>
      )}
      {canAddMore && (
        <Button
          color='primary'
          disabled={isRemoving}
          sx={{ alignSelf: 'flex-start' }}
          variant='outlined'
          onClick={() => {
            setShowLinkingPanel(true);
            setRemoveError(null);
          }}
        >
          {ownConnections.length === 0 ? t('button_add') : t('button_add_another')}
        </Button>
      )}
    </Box>
  );
};
